import { createContext } from 'react';
import type { ThemeMode } from '@/shared/domain/types';

export type ResolvedTheme = 'light' | 'dark';

export interface ThemeContextValue {
  /** Lo que eligió la persona: claro, oscuro o según el sistema. */
  mode: ThemeMode;
  /** Lo que se está mostrando de verdad (nunca 'system'). */
  resolved: ResolvedTheme;
  setMode: (mode: ThemeMode) => void;
  /** Alterna entre claro y oscuro a partir de lo que se ve ahora. */
  toggle: () => void;
}

export const ThemeContext = createContext<ThemeContextValue | null>(null);

/** Misma clave que lee `public/theme-init.js` antes de pintar (evita el parpadeo de tema). */
export const THEME_STORAGE_KEY = 'pizarra:theme';

const DARK_QUERY = '(prefers-color-scheme: dark)';

export const isThemeMode = (value: unknown): value is ThemeMode =>
  value === 'light' || value === 'dark' || value === 'system';

export function readStoredTheme(): ThemeMode | null {
  try {
    const value = window.localStorage.getItem(THEME_STORAGE_KEY);
    return isThemeMode(value) ? value : null;
  } catch {
    return null;
  }
}

export function writeStoredTheme(mode: ThemeMode): void {
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, mode);
  } catch {
    /* almacenamiento bloqueado (modo privado, cuota): el tema sigue funcionando en memoria */
  }
}

export const darkMediaQuery = (): MediaQueryList | null =>
  typeof window.matchMedia === 'function' ? window.matchMedia(DARK_QUERY) : null;

export const systemPrefersDark = (): boolean => darkMediaQuery()?.matches ?? false;

export const resolveTheme = (mode: ThemeMode, systemDark: boolean): ResolvedTheme =>
  mode === 'system' ? (systemDark ? 'dark' : 'light') : mode;

/** Color de fondo del tema leído de los tokens (`--c-bg`), listo para `<meta name="theme-color">`. */
function tokenBackground(root: HTMLElement): string | null {
  const channels = window.getComputedStyle(root).getPropertyValue('--c-bg').trim();
  return channels ? `rgb(${channels})` : null;
}

function syncThemeColorMeta(root: HTMLElement): void {
  const color = tokenBackground(root);
  if (!color) return;
  const metas = Array.from(
    document.head.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]'),
  );
  const [first, ...extra] = metas;
  const meta =
    first ??
    document.head.appendChild(
      Object.assign(document.createElement('meta'), { name: 'theme-color' }),
    );
  // Una sola etiqueta sin `media`: lo elegido en la app manda sobre el modo del sistema operativo.
  meta.removeAttribute('media');
  meta.content = color;
  extra.forEach((m) => m.remove());
}

/** Aplica el tema al documento: data-theme, clase `dark`, color-scheme y theme-color. */
export function applyThemeToDocument(resolved: ResolvedTheme): void {
  const root = document.documentElement;
  root.setAttribute('data-theme', resolved);
  root.classList.toggle('dark', resolved === 'dark');
  root.style.colorScheme = resolved;
  syncThemeColorMeta(root);
}
